import { useState, useEffect, useMemo, useRef } from 'react';
import { collectionGroup, collection, getDocs, onSnapshot } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import AppLayout from '../components/AppLayout';
import { Dropdown } from '../components/ui';
import { HeaderSearch, DateRangeButton } from '../components/HeaderControls';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { Paging } from '../components/ui/Paging';
import { computeDateWindow, inWindow, type DateFilterKey } from '../utils/ledgerReports';
import { downloadCSV } from '../utils/csv';
import { useSettings } from '../hooks/useSettings';
import { fmtMoney } from '../utils/currency';
import type { CodeHistoryEntry, CodeEventType, Entity, TransactionCategory } from '../types';

// Day/Month/Year, not the raw stored "YYYY-MM-DD" — e.g. "24 Sep 2026".
// Written-out month (not DD/MM/YYYY numeric) so the order is unambiguous —
// a pure numeric date reads differently depending on which locale's eyes
// are on it, which defeats the point of asking for one fixed order.
function fmtDateDMY(dateStr?: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// A read-only movement log — "what happened to my inventory, when, to
// whom, at what price." Reads the SAME append-only ledger the Split & Merge
// page's own History modal already uses (itemCodes/{codeId}/history), just
// across every event type instead of filtering to one, and as a full page
// instead of a modal. Keyed by Code, not by any one InventoryItem doc id,
// so a parcel's story ("purchased → sold") survives it being split along
// the way — see CodeHistoryEntry in types/index.ts.
//
// Known gap (by design, not an oversight): Stock Adjustments, Transfers
// and Write-offs don't write to this ledger yet, so they won't show up
// here (Revaluations do now). Purchase + Sale + everything already tracked (Memo
// Out/In, Split & Merge, Returns) is v1's scope.

const ACTIVITY_CAP = 1000;

// Default view is "This Month" (the shared DateRangeFilter chip) — everything
// within ACTIVITY_CAP is already fetched client-side (see the collectionGroup
// read below), so "load extra" is just clicking a wider chip (3 Months / This
// Year / All Time) — instant, no second fetch needed.

const EVENT_TYPES: CodeEventType[] = [
  'purchase', 'sale', 'memo-out', 'memo-in', 'split-out', 'split-in', 'return-out', 'return-in', 'revaluation', 'dummy-credit', 'dummy-debit',
];
const KNOWN_EVENT_TYPES = new Set<string>(EVENT_TYPES);

// Same labels/colors as Memos.tsx's own EVENT_LABEL/EVENT_COLOR (duplicated
// per this app's per-page-helper convention) — keeps the same event reading
// the same way wherever it's shown.
const EVENT_LABEL: Record<CodeEventType, string> = {
  'purchase':   'Purchased',
  'memo-out':   'Sent on Memo',
  'memo-in':    'Returned',
  'sale':       'Sold',
  'split-out':  'Split Out',
  'split-in':   'Split In',
  'return-out': 'Returned to Supplier',
  'return-in':  'Returned by Customer',
  'dummy-credit': 'Cash Credit (Books Adj.)',
  'dummy-debit':  'Cash Debit (Books Adj.)',
  'revaluation':  'Revalued',
};
const EVENT_COLOR: Record<CodeEventType, string> = {
  'purchase':   'var(--primary)',
  'memo-out':   '#c2410c',
  'memo-in':    'var(--text-2)',
  'sale':       'var(--success)',
  'split-out':  'var(--warning)',
  'split-in':   'var(--primary)',
  'return-out': 'var(--danger)',
  'return-in':  'var(--success)',
  'dummy-credit': 'var(--danger)',
  'dummy-debit':  'var(--success)',
  'revaluation':  'var(--warning)',
};

// A 'purchase' entry's notes read "Lot {invoiceNo}" — the full invoice
// number, same value CSV/Purchase writes verbatim (per this app's own
// "the full value is always what's saved, only the display is truncated"
// rule — see lotNoDisplay-style helpers elsewhere). Truncates just the
// trailing identifier to its last 4 characters for display, leaving any
// text before it (e.g. "Lot ", "To code ") untouched.
function shortNotes(notes?: string): string {
  if (!notes) return '';
  const m = notes.match(/^(.*\s)?(\S+)$/);
  if (!m) return notes;
  const [, prefix = '', lastToken] = m;
  return lastToken.length > 4 ? `${prefix}${lastToken.slice(-4)}` : notes;
}

function EventBadge({ type }: { type: CodeEventType }) {
  const color = EVENT_COLOR[type];
  return (
    <span className="badge" style={{
      background: `color-mix(in srgb, ${color} 14%, transparent)`,
      color, border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
      fontSize: 11, fontWeight: 700,
    }}>
      {EVENT_LABEL[type]}
    </span>
  );
}

// Same three icons/colors as Purchase.tsx's own Category picker (duplicated
// per this app's per-page-icon convention) — 'purchase', 'sale',
// 'dummy-credit' and 'dummy-debit' entries carry a category (see
// CodeHistoryEntry); every other event type shows a blank cell here, same
// as a missing value elsewhere in this table.
function IconCatBooksReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M4.2 7.2l2 2 3.6-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCatCashReal() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3.6 3.6l6.8 6.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconCatBooksDummy() {
  return <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
    <path d="M3.5 1.5h4.5l2.5 2.5v8a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1v-9.5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 1.5v2.5h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M5 8.3c.3-.5 1-.6 1.4-.3.5.3.5.9 0 1.2-.6.3-.6.9 0 1.2.4.3 1.1.2 1.4-.3M6.6 7v5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
  </svg>;
}
const CATEGORY_META: Record<TransactionCategory, { label: string; color: string; icon: React.ReactNode }> = {
  'books-real':  { label: 'By Books & Real',  color: 'var(--success)', icon: <IconCatBooksReal /> },
  'cash-real':   { label: 'Cash & Real',      color: 'var(--danger)',  icon: <IconCatCashReal /> },
  'books-dummy': { label: 'By Books & Dummy', color: 'var(--text-2)',  icon: <IconCatBooksDummy /> },
};
function CategoryCell({ category }: { category?: TransactionCategory }) {
  if (!category) return <span style={{ opacity: 0.4 }}>—</span>;
  const meta = CATEGORY_META[category];
  return (
    <span title={meta.label} style={{ display: 'inline-flex', alignItems: 'center', color: meta.color }}>
      {meta.icon}
    </span>
  );
}

function LotPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconEmptySearch() {
  return <svg width="36" height="36" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.1" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>;
}
function IconEmptyDiamond() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 14.5V6M1.5 6h13M4 2l4 4M12 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// Same shape as every other list page's Pagination — duplicated per this
// app's per-page-helper convention (see Purchase.tsx/Sales.tsx/AP.tsx).
interface Row { id: string; codeId: string; data: CodeHistoryEntry }

// A line's saved time (Timestamp or ISO text) as millis; 0 when missing.
function savedMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  const o = v as { toMillis?: () => number; seconds?: number };
  if (typeof o.toMillis === 'function') return o.toMillis();
  return typeof o.seconds === 'number' ? o.seconds * 1000 : 0;
}

export default function InventoryActivityPage() {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState<Entity[]>([]);

  const [dateFilter, setDateFilter] = useState<DateFilterKey>('this-month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [printAll, setPrintAll] = useState(false);

  // One-shot collectionGroup read — same call InventoryMerge.tsx's History
  // modal already makes (no where()/orderBy(), so no composite index is
  // needed), just not scoped to one event type. The client-side type filter
  // below is what keeps the unrelated skus/{skuId}/history revaluation docs
  // (type: 'revaluation') out, same trick that modal already relies on.
  useEffect(() => {
    const db = getClientDb();
    if (!db) { setLoading(false); return; }
    let cancelled = false;
    getDocs(collectionGroup(db, 'history'))
      .then(snap => {
        if (cancelled) return;
        const list = snap.docs
          // Filtering on `type` alone isn't enough — skus/{skuId}/history
          // (SkuHistoryEntry, a loose-only SKU cost ledger, unrelated to
          // this Code-keyed one) also uses the literal string 'purchase'
          // for its own, differently-shaped entries. Left unchecked, those
          // leaked through here for every loose parcel with a real bug: its
          // parent doc id is the SKU, not the Code, so the Code column
          // silently showed the SKU instead. Checking the entry's actual
          // grandparent collection (itemCodes, not skus) is what actually
          // excludes them — matching `type` against every CodeEventType is
          // just cheap defense-in-depth on top of that, not the real guard.
          .filter(d => d.ref.parent.parent?.parent.id === 'itemCodes')
          .map(d => ({ id: d.id, codeId: d.ref.parent.parent?.id ?? '', data: d.data() as CodeHistoryEntry }))
          .filter(r => KNOWN_EVENT_TYPES.has(r.data.type))
          // Newest first: by date, then by the time it was saved (never shown).
          .sort((a, b) => (b.data.date ?? '').localeCompare(a.data.date ?? '')
            || savedMillis(b.data.createdAt) - savedMillis(a.data.createdAt) || b.id.localeCompare(a.id))
          .slice(0, ACTIVITY_CAP);
        setRows(list);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    return onSnapshot(collection(db, 'entities'), snap => {
      setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity)));
    });
  }, []);

  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  const dateRange = useMemo(() => computeDateWindow(dateFilter, customFrom, customTo), [dateFilter, customFrom, customTo]);

  const baseRows = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return rows.filter(r => {
      if (!inWindow(r.data.date, dateRange ?? undefined)) return false;
      if (q && !r.codeId.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, dateRange, searchQ]);

  // Excel-style filters (replaced the header's Event menu).
  const filterColumns = useMemo<FilterColumn<Row>[]>(() => [
    { key: 'cat',   label: 'Cat.',         get: r => r.data.category ? CATEGORY_META[r.data.category].label : '' },
    { key: 'code',  label: 'Code',         get: r => r.codeId },
    { key: 'event', label: 'Event',        get: r => EVENT_LABEL[r.data.type] },
    { key: 'party', label: 'Counterparty', get: r => r.data.entityId ? entityName(r.data.entityId) : '' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows: filtered, filters: colFilters } = useColumnFilters(baseRows, filterColumns);
  const paging = usePaging(filtered, `${searchQ}|${dateFilter}|${customFrom}|${customTo}|${colFilters.stamp}`);
  // The table scrolls inside and the page numbers stay at the bottom of the screen.
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [filtered.length > 0, loading, colFilters.active.length]);

  // Printing the on-screen page would only print PAGE_SIZE rows — bypass
  // pagination for the print pass (every filtered/sorted row instead), same
  // two-rAF-then-print technique every other list page's Print action uses.
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    window.addEventListener('afterprint', after);
    requestAnimationFrame(() => {
      if (cancelled) return;
      requestAnimationFrame(() => { if (!cancelled) window.print(); });
    });
    return () => { cancelled = true; window.removeEventListener('afterprint', after); };
  }, [printAll]);

  const displayedRows = printAll ? filtered : paging.pageRows;

  // Buying side events carry what we paid (Cost/ct); the rest a selling price.
  const costSide = (t: string) => t === 'purchase' || t === 'split-in' || t === 'split-out' || t === 'return-out';

  function handleExportCSV() {
    downloadCSV(
      'inventory-activity',
      ['Date', 'Category', 'Code', 'Event', 'Carat', 'Counterparty', 'Cost/ct', 'Price/ct', 'Amount', 'Notes'],
      filtered.map(r => [
        r.data.date ?? '', r.data.category ? CATEGORY_META[r.data.category].label : '',
        r.codeId, EVENT_LABEL[r.data.type], (r.data.carat ?? 0).toFixed(2),
        entityName(r.data.entityId),
        costSide(r.data.type) && r.data.pricePerCt != null ? r.data.pricePerCt.toFixed(2) : '',
        !costSide(r.data.type) && r.data.pricePerCt != null ? r.data.pricePerCt.toFixed(2) : '',
        (r.data.amountUSD ?? 0).toFixed(2),
        shortNotes(r.data.notes),
      ]),
    );
  }

  return (
    <AppLayout
      title="Inventory Activity"
      subtitle="Every purchase, sale, memo, split and return event — one continuous timeline per Code. Shows this month by default; widen the date range for older activity."
      toolbar={<>
        <HeaderSearch value={searchQ} onChange={setSearchQ} placeholder="Search Code…" label="Search by code" />
        <DateRangeButton
          value={dateFilter} onChange={setDateFilter}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
        {(searchQ || dateFilter !== 'this-month' || colFilters.active.length > 0) && (
          <button type="button" className="tb-clear" onClick={() => { setSearchQ(''); setDateFilter('this-month'); colFilters.clear(); }}>Clear</button>
        )}
      </>}
      headerActions={
        <div className="tb-group">
          <Dropdown
            align="right"
            trigger={() => (
              <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExport /></button>
            )}
          >
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
      }
    >
      {colFilters.active.length > 0 && (
        <div className="no-print" style={{ display: 'flex', marginBottom: 8 }}>
          <ColumnFilterChips filters={colFilters} inline />
        </div>
      )}
      {loading ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {['Date', 'Cat.', 'Code', 'Event', 'Carat', 'Counterparty', 'Cost/ct', 'Price/ct', 'Amount', 'Notes'].map(h => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>{[...Array(6)].map((_, i) => (
              <tr key={i} style={{ opacity: 1 - i * 0.14 }}>
                {[...Array(10)].map((_, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 13, width: '80%', borderRadius: 4 }} /></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--text-3)' }}>
            {rows.length === 0 ? <IconEmptyDiamond /> : <IconEmptySearch />}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>
            {rows.length === 0 ? 'No inventory activity yet' : 'No events match your filters'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 340, margin: '0 auto' }}>
            {rows.length === 0
              ? 'Purchases, sales, memos, splits and returns will show up here as they happen.'
              : 'Try widening the date range or clearing a filter.'}
          </div>
        </div>
      ) : (
        <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th className="th-filterable" style={{ textAlign: 'center' }} title="Transaction category">Cat.<ColumnFilterButton filters={colFilters} colKey="cat" label="Cat." /></th>
                <th className="th-filterable">Code<ColumnFilterButton filters={colFilters} colKey="code" label="Code" /></th>
                <th className="th-filterable">Event<ColumnFilterButton filters={colFilters} colKey="event" label="Event" /></th>
                <th style={{ textAlign: 'right' }}>Carat</th>
                <th className="th-filterable">Counterparty<ColumnFilterButton filters={colFilters} colKey="party" label="Counterparty" /></th>
                <th style={{ textAlign: 'right' }} title="Purchase, split and supplier return: what we paid per ct">Cost/ct</th>
                <th style={{ textAlign: 'right' }} title="Sale, memo and customer return: the price per ct">Price/ct</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.data.date ? fmtDateDMY(r.data.date) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ textAlign: 'center' }}><CategoryCell category={r.data.category} /></td>
                  <td><LotPill>{r.codeId || '—'}</LotPill></td>
                  <td><EventBadge type={r.data.type} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{(r.data.carat ?? 0).toFixed(2)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.data.entityId ? entityName(r.data.entityId) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                    {costSide(r.data.type) && r.data.pricePerCt != null ? money(r.data.pricePerCt) : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                    {!costSide(r.data.type) && r.data.pricePerCt != null ? money(r.data.pricePerCt) : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>
                    {r.data.amountUSD != null ? money(r.data.amountUSD) : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-3)' }} title={r.data.notes}>{r.data.notes ? shortNotes(r.data.notes) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={10} style={{ fontWeight: 700, color: 'var(--text-1)' }}>
                  Total · {filtered.length} event{filtered.length === 1 ? '' : 's'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && !printAll && (
        <div className="no-print">
          <Paging page={paging.page} totalPages={paging.totalPages} total={paging.total} onChange={paging.setPage} noun="event" />
        </div>
      )}
    </AppLayout>
  );
}
